import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, RefreshCw, LayoutGrid, PackageCheck, AlertTriangle } from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vending`;

// v2.5.864 — Planograms page (V3 preview)
// Grid view of a machine's slots + par-level vs current qty. The full drag-drop
// visual editor lands in Sprint V3; this preview provides read/write on slots
// so operators can already build & tweak their planograms.
export default function VendingPlanogramsPage() {
  const [machines, setMachines] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [slots, setSlots] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMachines = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([axios.get(`${API}/machines`), axios.get(`${API}/products`)]);
      setMachines(m.data.items || []);
      setProducts(p.data.items || []);
      if (!selectedId && m.data.items?.length) setSelectedId(m.data.items[0].id);
    } catch (e) {
      if (e.response?.status !== 402) {
        toast.error(e.response?.data?.detail || 'Could not load machines');
      }
    } finally { setLoading(false); }
  }, [selectedId]);

  const loadSlots = useCallback(async () => {
    if (!selectedId) { setSlots([]); return; }
    try {
      const { data } = await axios.get(`${API}/slots`, { params: { machine_id: selectedId } });
      setSlots(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load planogram');
    }
  }, [selectedId]);

  useEffect(() => { loadMachines(); }, [loadMachines]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  const productById = (pid) => products.find(p => p.id === pid);
  const lowStock = slots.filter(s => s.par_level > 0 && s.current_qty <= s.par_level * 0.3);

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="vending-planograms-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <LayoutGrid className="h-5 w-5 sm:h-6 sm:w-6 text-violet-600 flex-shrink-0" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" data-testid="vending-planograms-title">
              Planograms<PageInfoTooltip pageKey="vending-planograms" />
            </h1>
            <Badge className="bg-violet-100 text-violet-800 border-0 text-[10px] sm:text-xs">V3 · Preview</Badge>
          </div>
          <p className="text-xs sm:text-sm text-gray-medium">Slot-level SKU allocation, par levels &amp; current stock.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { loadMachines(); loadSlots(); }} className="h-9 text-xs sm:text-sm" data-testid="planograms-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {loading && <div className="text-sm text-gray-medium py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading fleet…</div>}

      {!loading && machines.length === 0 && (
        <Card><CardContent className="p-6 text-center text-sm text-gray-medium">
          No machines yet. Add a machine first from <b>Vending Fleet → Machines</b>.
        </CardContent></Card>
      )}

      {!loading && machines.length > 0 && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="text-xs sm:text-sm text-gray-medium">Machine:</div>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="h-9 text-sm max-w-sm" data-testid="planogram-machine-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {machines.map(m => <SelectItem key={m.id} value={m.id}>{m.chassis_serial} — {m.vendor} {m.model}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-xs text-gray-medium flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-[10px]">{slots.length} slots</Badge>
              {lowStock.length > 0 && (
                <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {lowStock.length} low
                </Badge>
              )}
            </div>
          </div>

          {slots.length === 0 && (
            <Card><CardContent className="p-6 text-center text-sm text-gray-medium">
              This machine has no planogram yet. Slots can be created via <code className="text-xs">POST /api/vending/slots</code>.
              The drag-and-drop editor lands in <b>Sprint V3</b>.
            </CardContent></Card>
          )}

          {slots.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3" data-testid="planogram-grid">
              {slots.map(s => {
                const p = productById(s.product_id);
                const pct = s.par_level > 0 ? Math.min(100, (s.current_qty / s.par_level) * 100) : 0;
                const low = s.par_level > 0 && s.current_qty <= s.par_level * 0.3;
                return (
                  <Card key={s.id} className={low ? 'border-amber-300' : ''} data-testid={`slot-${s.id}`}>
                    <CardContent className="p-3 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="font-bold text-sm tabular-nums">{s.slot_code}</div>
                        {low && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                      </div>
                      <div className="text-[11px] font-medium truncate">{p?.name || <span className="text-gray-400">empty</span>}</div>
                      <div className="text-[10px] text-gray-medium truncate font-mono">{p?.sku || '—'}</div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${low ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-[10px] flex justify-between text-gray-medium">
                        <span>{s.current_qty}/{s.par_level}</span>
                        <span className="tabular-nums">{p?.currency || ''} {s.price?.toFixed(2) || '—'}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
